import { intlLocale } from '@/i18n'
import type { LeaveCalendarData, LeaveCalendarEntry } from './types'

/** Local-date helpers working on Y-m-d strings (no time-zone drift). */

export function toYmd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export function parseYmd(value: string): Date {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

export function monthBounds(month: Date): { from: string; to: string } {
  return {
    from: toYmd(new Date(month.getFullYear(), month.getMonth(), 1)),
    to: toYmd(new Date(month.getFullYear(), month.getMonth() + 1, 0)),
  }
}

export function addMonths(month: Date, n: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + n, 1)
}

/** All dates of the month as Y-m-d strings. */
export function daysOfMonth(month: Date): string[] {
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  return Array.from({ length: last }, (_, i) => toYmd(new Date(month.getFullYear(), month.getMonth(), i + 1)))
}

/** Counts school days in [start, end] given closed weekdays (0 = Sunday). Mirrors the API rule. */
export function countWorkingDays(
  start: string,
  end: string,
  closedWeekdays: number[],
  halfDay = false,
): number {
  if (!start || !end || end < start) return 0
  let n = 0
  for (
    let d = parseYmd(start);
    toYmd(d) <= end;
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
  ) {
    if (!closedWeekdays.includes(d.getDay())) n++
  }
  return halfDay && n > 0 ? 0.5 : n
}

/** Weekday names (Monday first) in the active interface language; `js` is Date#getDay(). */
export function weekdaysMondayFirst(): { short: string; long: string; js: number }[] {
  const short = new Intl.DateTimeFormat(intlLocale(), { weekday: 'short' })
  const long = new Intl.DateTimeFormat(intlLocale(), { weekday: 'long' })
  // 2024-01-01 was a Monday.
  return [1, 2, 3, 4, 5, 6, 0].map((js, i) => {
    const d = new Date(2024, 0, 1 + i)
    return { short: short.format(d), long: long.format(d), js }
  })
}

/** Groups entries by the open days they cover. */
export function awayByDay(
  days: string[],
  data: LeaveCalendarData | undefined,
): Map<string, LeaveCalendarEntry[]> {
  const map = new Map<string, LeaveCalendarEntry[]>()
  if (!data) return map
  for (const day of days) {
    if (data.closedWeekdays.includes(parseYmd(day).getDay())) continue
    const list = data.entries.filter((e) => e.startDate <= day && e.endDate >= day)
    if (list.length) map.set(day, list)
  }
  return map
}
